/**
 * Punch-list f10 — per-study configurable milestone tracking.
 *
 * The client asked for a "configurable set per study" rather than one
 * fixed global list of milestone types, so `milestone_name` here is
 * free text the study defines for itself (e.g. "First Patient In",
 * "IRB Approval", "Database Lock") — there is no shared catalog.
 *
 * Write is gated the same as AdverseEventConfigPanel/DoaResponsibilitiesPanel
 * (site_admin / study designer / regulatory / PI / coordinator / platform);
 * read is open to everyone who can view the study.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarCheck, Loader2, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import { studyMilestones, type StudyMilestoneStatus } from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { fmtDate } from '@/lib/dates';
import { errorText } from '@/lib/permissionError';

const WRITE_ROLES = [
  'site_study_designer',
  'regulatory_specialist',
  'principal_investigator',
  'sub_investigator',
  'coordinator',
] as const;

const STATUS_LABEL: Record<StudyMilestoneStatus, string> = {
  pending: 'Pending',
  completed: 'Completed',
  at_risk: 'At risk',
  missed: 'Missed',
};

const STATUS_BADGE: Record<StudyMilestoneStatus, string> = {
  pending: 'bg-surface-muted text-ink-secondary border-surface-border',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  at_risk: 'bg-amber-50 text-amber-700 border-amber-200',
  missed: 'bg-red-50 text-red-700 border-red-200',
};

export function StudyMilestonesPanel({ studyId, readOnly = false }: { studyId: string; readOnly?: boolean }) {
  const canWrite = useHasRole(WRITE_ROLES) && !readOnly;
  const qc = useQueryClient();
  /**
   * One dialog serves add and edit. `null` closed, `'new'` adding, otherwise
   * the milestone_id being edited.
   *
   * Editing exists because studies are now seeded with the standard set at
   * creation (services/studies/seedMilestones.ts). Those rows arrive with no
   * dates, and the panel previously rendered both dates as read-only text with
   * status as the only mutation — so a seeded milestone could never be filled
   * in. The backend update endpoint already accepted all of these fields.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ milestone_name: '', target_date: '', actual_date: '', notes: '' });

  /** `YYYY-MM-DD` for a date input; the API returns a full ISO timestamp. */
  const toDateInput = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : '');

  const openAdd = () => {
    setDraft({ milestone_name: '', target_date: '', actual_date: '', notes: '' });
    setEditing('new');
  };
  const openEdit = (m: {
    milestone_id: string; milestone_name: string;
    target_date: string | null; actual_date: string | null; notes: string | null;
  }) => {
    setDraft({
      milestone_name: m.milestone_name,
      target_date: toDateInput(m.target_date),
      actual_date: toDateInput(m.actual_date),
      notes: m.notes ?? '',
    });
    setEditing(m.milestone_id);
  };

  const q = useQuery({
    queryKey: ['study', studyId, 'milestones'],
    queryFn: () => studyMilestones.list(studyId),
    enabled: !!studyId,
  });
  const rows = q.data?.rows ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['study', studyId, 'milestones'] });

  const createMut = useMutation({
    mutationFn: () =>
      studyMilestones.create(studyId, {
        milestone_name: draft.milestone_name.trim(),
        target_date: draft.target_date || undefined,
        notes: draft.notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(`Milestone "${draft.milestone_name}" added`);
      setEditing(null);
      invalidate();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to add milestone')),
  });

  const editMut = useMutation({
    mutationFn: (milestoneId: string) =>
      studyMilestones.update(studyId, milestoneId, {
        milestone_name: draft.milestone_name.trim(),
        // Empty clears the date; the endpoint takes null for exactly that.
        target_date: draft.target_date || null,
        actual_date: draft.actual_date || null,
        notes: draft.notes.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Milestone updated');
      setEditing(null);
      invalidate();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to update milestone')),
  });

  const statusMut = useMutation({
    mutationFn: ({ milestoneId, status }: { milestoneId: string; status: StudyMilestoneStatus }) =>
      studyMilestones.update(studyId, milestoneId, { status }),
    onSuccess: invalidate,
    onError: (err) => toast.error(errorText(err, 'Failed to update status')),
  });

  const deleteMut = useMutation({
    mutationFn: (milestoneId: string) => studyMilestones.delete(studyId, milestoneId),
    onSuccess: () => {
      toast.success('Milestone removed');
      invalidate();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to remove milestone')),
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between border-b border-surface-border bg-surface-muted/50 px-4 py-3">
        <div className="flex items-center gap-2">
          <CalendarCheck className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">Milestones</h2>
        </div>
        {canWrite && (
          <button type="button" className="btn-secondary text-xs" onClick={openAdd}>
            <Plus className="h-3.5 w-3.5" /> Add milestone
          </button>
        )}
      </div>

      {q.isLoading && (
        <div className="flex items-center justify-center gap-2 p-6 text-xs text-ink-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading milestones…
        </div>
      )}

      {!q.isLoading && rows.length === 0 && (
        <div className="p-6 text-center text-sm text-ink-muted">
          No milestones yet. Studies created from now on start with the standard set; add the
          ones that matter for this study — there's no fixed list.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-surface-border bg-surface-muted/30 text-[10px] uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Milestone</th>
                <th className="px-3 py-2 font-medium">Target date</th>
                <th className="px-3 py-2 font-medium">Actual date</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Notes</th>
                {canWrite && <th className="px-3 py-2 font-medium"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {rows.map((m) => (
                <tr key={m.milestone_id}>
                  <td className="px-3 py-2 align-top font-medium text-ink-primary">{m.milestone_name}</td>
                  <td className="px-3 py-2 align-top text-ink-muted">
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => openEdit(m)}
                        className="rounded px-1 -mx-1 hover:bg-surface-muted hover:text-ink-primary"
                        title="Set the target date"
                      >
                        {m.target_date ? fmtDate(m.target_date) : 'Set date'}
                      </button>
                    ) : (
                      m.target_date ? fmtDate(m.target_date) : '—'
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-ink-muted">
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => openEdit(m)}
                        className="rounded px-1 -mx-1 hover:bg-surface-muted hover:text-ink-primary"
                        title="Set the actual date"
                      >
                        {m.actual_date ? fmtDate(m.actual_date) : 'Set date'}
                      </button>
                    ) : (
                      m.actual_date ? fmtDate(m.actual_date) : '—'
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {canWrite ? (
                      <select
                        className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${STATUS_BADGE[m.status]}`}
                        value={m.status}
                        disabled={statusMut.isPending}
                        onChange={(e) =>
                          statusMut.mutate({ milestoneId: m.milestone_id, status: e.target.value as StudyMilestoneStatus })
                        }
                      >
                        {(Object.keys(STATUS_LABEL) as StudyMilestoneStatus[]).map((s) => (
                          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[m.status]}`}>
                        {STATUS_LABEL[m.status]}
                      </span>
                    )}
                  </td>
                  <td className="max-w-[240px] truncate px-3 py-2 align-top text-ink-muted" title={m.notes || undefined}>
                    {m.notes || '—'}
                  </td>
                  {canWrite && (
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(m)}
                          className="text-ink-muted hover:text-ink-primary"
                          aria-label={`Edit ${m.milestone_name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Remove "${m.milestone_name}"?`)) deleteMut.mutate(m.milestone_id);
                          }}
                          className="text-ink-muted hover:text-danger-700"
                          aria-label="Remove milestone"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.milestone_name.trim()) {
                toast.error('Milestone name is required.');
                return;
              }
              if (editing === 'new') createMut.mutate();
              else editMut.mutate(editing);
            }}
            className="card w-full max-w-md space-y-4 p-6"
          >
            <h3 className="text-lg font-semibold">
              {editing === 'new' ? 'Add milestone' : 'Edit milestone'}
            </h3>
            <div>
              <label className="label text-xs">Milestone name *</label>
              <input
                className="input"
                maxLength={200}
                value={draft.milestone_name}
                onChange={(e) => setDraft({ ...draft, milestone_name: e.target.value })}
                placeholder="e.g. First Patient In"
                autoFocus
              />
            </div>
            <div>
              <label className="label text-xs">Target date</label>
              <input
                type="date"
                className="input"
                value={draft.target_date}
                onChange={(e) => setDraft({ ...draft, target_date: e.target.value })}
              />
            </div>
            {/* Only on edit: a milestone cannot already have happened when it
                is first written, and the create endpoint takes no actual_date. */}
            {editing !== 'new' && (
              <div>
                <label className="label text-xs">Actual date</label>
                <input
                  type="date"
                  className="input"
                  value={draft.actual_date}
                  onChange={(e) => setDraft({ ...draft, actual_date: e.target.value })}
                />
                <p className="mt-1 text-[11px] text-ink-muted">
                  Clear the field to unset a date.
                </p>
              </div>
            )}
            <div>
              <label className="label text-xs">Notes (optional)</label>
              <input
                className="input"
                maxLength={2000}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button
                type="submit"
                className="btn-primary"
                disabled={createMut.isPending || editMut.isPending}
              >
                {createMut.isPending || editMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editing === 'new' ? (
                  <Plus className="h-4 w-4" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {editing === 'new' ? 'Add milestone' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
